import React, { useState } from 'react';
import { Badge, Button, Card, Icon, Input, PulseDot } from '../ui';
import { timeAgo } from '../../lib/comms';
import { canReviewRun, fetchHostingRunSteps, sitePlatform, type HostedSite, type HostingRun, type HostingRunStep, type HostingStepStatus } from '../../lib/hosting';
import RunStatusBadge from './RunStatusBadge';
import StepDetail from './StepDetail';
import FixPlan, { type FixPlanContext } from './FixPlan';
import { dateTime, duration, host, runMode } from './runFormat';

// A running run whose runner hasn't written a heartbeat in this long is probably stuck.
const STALE_HEARTBEAT_MS = 3 * 60_000;

export interface HostingRunsCardProps {
  runs: HostingRun[];
  /** The latest run's steps (polled by the page while it is live). */
  steps: HostingRunStep[];
  loaded: boolean;
  error: string | null;
  /** The account's websites, to name each step group. */
  sites: HostedSite[];
  runnableCount: number;
  /** Sign a finished attention/failed run off, or take the sign-off back. */
  onReview?: (run: HostingRun, reviewed: boolean, note: string) => Promise<void>;
  /** Lets the plain-English read-out send agent-fixable findings to the agent queue. */
  fixCtx?: FixPlanContext;
}

/** Checklist runs: the latest one open with its steps, older ones folded below. */
export default function HostingRunsCard({ runs, steps, loaded, error, sites, runnableCount, onReview, fixCtx }: HostingRunsCardProps) {
  const [latest, ...older] = runs;
  return (
    <Card
      eyebrow={sites.length ? `${runnableCount} of ${sites.length} site${sites.length === 1 ? '' : 's'} linked` : 'Managed hosting checklist'}
      title="Checklist runs"
    >
      {error ? (
        <Empty tone="down">{error}</Empty>
      ) : !loaded ? (
        <Empty>Loading runs…</Empty>
      ) : !latest ? (
        <Empty>
          {runnableCount
            ? 'No checklist runs yet. Run checklist starts one.'
            : 'No site linked yet. Once a website is linked to its Cloudways app or Netlify site, the checklist can run here.'}
        </Empty>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
          <RunBody run={latest} steps={steps} sites={sites} onReview={onReview} fixCtx={fixCtx} />
          {older.length ? (
            <div style={{ borderTop: '1px solid var(--border-hairline)', paddingTop: 'var(--space-4)' }}>
              <div className="t-eyebrow" style={{ marginBottom: 'var(--space-2)' }}>
                Earlier runs
              </div>
              {older.map((r, i) => (
                <OlderRun key={r.id} run={r} sites={sites} first={i === 0} onReview={onReview} fixCtx={fixCtx} />
              ))}
            </div>
          ) : null}
        </div>
      )}
    </Card>
  );
}

/** One run's header facts, summary, error and grouped steps. */
function RunBody({
  run,
  steps,
  sites,
  onReview,
  fixCtx,
}: {
  run: HostingRun;
  steps: HostingRunStep[];
  sites: HostedSite[];
  onReview?: HostingRunsCardProps['onReview'];
  fixCtx?: FixPlanContext;
}) {
  const live = run.status === 'queued' || run.status === 'running';
  const done = steps.filter((s) => s.status !== 'pending' && s.status !== 'running').length;
  const stale = run.status === 'running' && run.heartbeat_at && Date.now() - new Date(run.heartbeat_at).getTime() > STALE_HEARTBEAT_MS;
  const took = duration(run.started_at, run.finished_at);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
        <RunStatusBadge status={run.status} reviewed={Boolean(run.reviewed_at)} />
        <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>
          {runMode(run.options)} · {timeAgo(run.created_at)}
        </span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(128px, 1fr))', gap: '12px 16px' }}>
        <Fact label="Requested by">{run.requested_by_name || '—'}</Fact>
        <Fact label="Started">{run.started_at ? dateTime(run.started_at) : run.status === 'queued' ? 'Waiting for runner' : '—'}</Fact>
        <Fact label="Finished">{dateTime(run.finished_at)}</Fact>
        <Fact label={live ? 'Elapsed' : 'Duration'}>{took ?? '—'}</Fact>
      </div>

      {run.status === 'queued' ? (
        <p style={{ margin: 0, fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>
          Queued {dateTime(run.created_at)}. The hosting runner picks it up within a minute when the operations Mac is awake.
        </p>
      ) : null}
      {run.status === 'cancelled' ? (
        <p style={{ margin: 0, fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>Cancelled before the runner picked it up.</p>
      ) : null}
      {run.summary ? <p style={{ margin: 0, fontSize: 'var(--text-sm)', color: 'var(--text-body)', lineHeight: 'var(--leading-normal)' }}>{run.summary}</p> : null}
      {run.error ? <Callout tone="down">{run.error}</Callout> : null}
      {steps.length && !live ? <FixPlan run={run} steps={steps} sites={sites} ctx={fixCtx} /> : null}
      {onReview && canReviewRun(run) ? <ReviewBar run={run} onReview={onReview} /> : null}
      {stale ? <Callout tone="warn">No heartbeat from {run.runner || 'the runner'} since {dateTime(run.heartbeat_at)}. It may have stopped.</Callout> : null}

      {run.status === 'running' && steps.length ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{ flex: 1, height: '4px', borderRadius: 'var(--radius-pill)', background: 'var(--surface-active)', overflow: 'hidden' }}>
            <div style={{ width: `${(done / steps.length) * 100}%`, height: '100%', background: 'var(--status-info)', transition: 'width 400ms var(--ease-out)' }} />
          </div>
          <span className="t-num" style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>
            {done} of {steps.length} steps
          </span>
        </div>
      ) : null}

      {steps.length ? <RunSteps steps={steps} sites={sites} /> : run.status === 'running' ? <Empty>Starting…</Empty> : null}
    </div>
  );
}

type Group = { key: string; label: string; url: string | null; platform: string | null; steps: HostingRunStep[] };

/** Account-level steps first, then one group per site in the order the runner reached them. */
function groupSteps(steps: HostingRunStep[], sites: HostedSite[]): Group[] {
  const groups = new Map<string, Group>();
  const sorted = [...steps].sort((a, b) => a.position - b.position);
  if (sorted.some((s) => !s.site_id)) groups.set('', { key: '', label: 'Account', url: null, platform: null, steps: [] });
  for (const s of sorted) {
    const key = s.site_id ?? '';
    if (!groups.has(key)) {
      const site = sites.find((x) => x.id === key);
      groups.set(key, { key, label: site ? host(site.url) : 'Removed site', url: site?.url ?? null, platform: sitePlatform(site), steps: [] });
    }
    groups.get(key)!.steps.push(s);
  }
  return [...groups.values()];
}

function RunSteps({ steps, sites }: { steps: HostingRunStep[]; sites: HostedSite[] }) {
  // Attention and failures open by default; a click overrides either way.
  const [toggled, setToggled] = useState<Record<string, boolean>>({});
  const isOpen = (s: HostingRunStep) => toggled[s.id] ?? (s.status === 'attention' || s.status === 'failed');
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
      {groupSteps(steps, sites).map((g) => (
        <div key={g.key || 'account'}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
            <Icon name={g.key ? 'globe' : 'building-2'} size={12} style={{ color: 'var(--text-faint)' }} />
            {g.url ? (
              <a href={g.url} target="_blank" rel="noopener noreferrer" className="t-eyebrow hover-underline" style={{ textTransform: 'none' }}>
                {g.label}
              </a>
            ) : (
              <span className="t-eyebrow">{g.label}</span>
            )}
            {g.platform ? (
              <span
                className="t-num"
                style={{
                  marginLeft: '2px',
                  padding: '1px 6px',
                  borderRadius: 'var(--radius-pill)',
                  background: 'var(--surface-raised)',
                  color: 'var(--text-muted)',
                  fontSize: 'var(--text-2xs)',
                  lineHeight: '14px',
                }}
              >
                {g.platform}
              </span>
            ) : null}
          </div>
          <div style={{ border: '1px solid var(--border-hairline)', borderRadius: 'var(--radius-md)' }}>
            {g.steps.map((s, i) => (
              <StepRow
                key={s.id}
                step={s}
                first={i === 0}
                open={isOpen(s)}
                onToggle={() => setToggled((t) => ({ ...t, [s.id]: !isOpen(s) }))}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

const STEP_ICON: Record<HostingStepStatus, { icon: string; color: string; label: string }> = {
  pending: { icon: 'circle-dot', color: 'var(--text-faint)', label: 'Pending' },
  running: { icon: '', color: 'var(--status-info)', label: 'Running' },
  passed: { icon: 'check-circle-2', color: 'var(--status-up)', label: 'Passed' },
  attention: { icon: 'triangle-alert', color: 'var(--status-warn)', label: 'Needs attention' },
  failed: { icon: 'circle-alert', color: 'var(--status-down)', label: 'Failed' },
  skipped: { icon: 'circle-dot', color: 'var(--text-faint)', label: 'Skipped' },
};

function StepRow({ step, first, open, onToggle }: { step: HostingRunStep; first: boolean; open: boolean; onToggle: () => void }) {
  const look = STEP_ICON[step.status] ?? STEP_ICON.pending;
  const hasDetail = Boolean(step.detail && Object.keys(step.detail).length);
  const took = step.status === 'running' || step.status === 'skipped' ? null : duration(step.started_at, step.finished_at);
  const quiet = step.status === 'pending' || step.status === 'skipped';
  return (
    <div style={{ borderTop: first ? 'none' : '1px solid var(--border-hairline)' }}>
      <button
        type="button"
        onClick={hasDetail ? onToggle : undefined}
        className={hasDetail ? 'hover-wash' : undefined}
        aria-expanded={hasDetail ? open : undefined}
        style={{
          width: '100%',
          display: 'grid',
          gridTemplateColumns: '16px 1fr auto',
          alignItems: 'start',
          gap: '10px',
          padding: '10px 12px',
          border: 0,
          background: 'transparent',
          borderRadius: 'var(--radius-md)',
          textAlign: 'left',
          cursor: hasDetail ? 'pointer' : 'default',
          fontFamily: 'inherit',
          color: 'inherit',
        }}
      >
        <span title={look.label} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', height: '18px', color: look.color }}>
          {step.status === 'running' ? <PulseDot tone="info" /> : <Icon name={look.icon} size={15} />}
        </span>
        <span style={{ minWidth: 0 }}>
          <span style={{ display: 'block', fontSize: 'var(--text-sm)', lineHeight: '18px', color: quiet ? 'var(--text-muted)' : 'var(--text-body)' }}>{step.title}</span>
          {step.summary ? (
            <span style={{ display: 'block', marginTop: '2px', fontSize: 'var(--text-xs)', color: 'var(--text-muted)', lineHeight: 'var(--leading-snug)', overflowWrap: 'anywhere' }}>
              {step.summary}
            </span>
          ) : null}
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', height: '18px' }}>
          {step.status === 'skipped' ? <Badge>Skipped</Badge> : null}
          {took ? (
            <span className="t-num" style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>
              {took}
            </span>
          ) : null}
          {hasDetail ? (
            <Icon name="chevron-down" size={14} style={{ color: 'var(--text-faint)', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 160ms ease' }} />
          ) : (
            <span style={{ width: '14px' }} />
          )}
        </span>
      </button>
      {open && hasDetail ? (
        <div className="pl-3 md:pl-[38px]" style={{ paddingTop: '2px', paddingRight: '12px', paddingBottom: '14px' }}>
          <StepDetail step={step} />
        </div>
      ) : null}
    </div>
  );
}

/** A folded earlier run; opening it loads its steps once. */
function OlderRun({ run, sites, first, onReview, fixCtx }: { run: HostingRun; sites: HostedSite[]; first: boolean; onReview?: HostingRunsCardProps['onReview']; fixCtx?: FixPlanContext }) {
  const [open, setOpen] = useState(false);
  const [steps, setSteps] = useState<HostingRunStep[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const toggle = () => {
    setOpen((o) => !o);
    if (steps || open) return;
    fetchHostingRunSteps(run.id)
      .then(setSteps)
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load the steps'));
  };

  const took = duration(run.started_at, run.finished_at);
  return (
    <div style={{ borderTop: first ? 'none' : '1px solid var(--border-hairline)' }}>
      <button
        type="button"
        onClick={toggle}
        // Phones fold the badge into the text column: a 104px badge column
        // left the date and finding ~150px and every row five lines tall
        className="hover-wash grid grid-cols-[14px_1fr_auto] md:grid-cols-[14px_104px_1fr_auto]"
        aria-expanded={open}
        style={{
          width: '100%',
          alignItems: 'center',
          gap: '10px',
          padding: '9px 6px',
          margin: '0 -6px',
          border: 0,
          background: 'transparent',
          borderRadius: 'var(--radius-sm)',
          textAlign: 'left',
          cursor: 'pointer',
          fontFamily: 'inherit',
          boxSizing: 'content-box',
        }}
      >
        <Icon name="chevron-right" size={14} style={{ color: 'var(--text-faint)', transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 160ms ease' }} />
        <span className="hidden md:block">
          <RunStatusBadge status={run.status} reviewed={Boolean(run.reviewed_at)} />
        </span>
        <span style={{ minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '1px' }}>
          <span className="md:hidden" style={{ marginBottom: '3px' }}>
            <RunStatusBadge status={run.status} reviewed={Boolean(run.reviewed_at)} />
          </span>
          {/* One line each on desktop; phones wrap, since a ~150px column
              cut the date and the finding to a few characters */}
          <span className="md:truncate" style={{ fontSize: 'var(--text-sm)', color: 'var(--text-body)' }}>
            {dateTime(run.created_at)} <span style={{ color: 'var(--text-faint)' }}>· {runMode(run.options)}{run.requested_by_name ? ` · ${run.requested_by_name}` : ''}</span>
          </span>
          {!open && (run.summary || run.error) ? (
            <span className="md:truncate" style={{ fontSize: 'var(--text-xs)', color: run.error ? 'var(--status-down)' : 'var(--text-muted)', overflowWrap: 'anywhere' }}>
              {run.error || run.summary}
            </span>
          ) : null}
        </span>
        <span className="t-num" style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>
          {took ?? ''}
        </span>
      </button>
      {open ? (
        // Phones reclaim the indent: the steps inside nest another 38px
        <div className="pl-0 md:pl-[24px]" style={{ paddingTop: '4px', paddingBottom: '16px' }}>
          {error ? (
            <Empty tone="down">{error}</Empty>
          ) : !steps ? (
            <Empty>Loading steps…</Empty>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
              {run.summary ? <p style={{ margin: 0, fontSize: 'var(--text-sm)', color: 'var(--text-body)' }}>{run.summary}</p> : null}
              {run.error ? <Callout tone="down">{run.error}</Callout> : null}
              {steps.length ? <FixPlan run={run} steps={steps} sites={sites} ctx={fixCtx} /> : null}
              {onReview && canReviewRun(run) ? <ReviewBar run={run} onReview={onReview} /> : null}
              {steps.length ? <RunSteps steps={steps} sites={sites} /> : <Empty>No steps recorded.</Empty>}
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Sign-off for a run that needed attention or failed: the history keeps what
 * the run found, the account reads healthy until the next run.
 */
function ReviewBar({ run, onReview }: { run: HostingRun; onReview: NonNullable<HostingRunsCardProps['onReview']> }) {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const act = async (reviewed: boolean) => {
    setBusy(true);
    try {
      await onReview(run, reviewed, note);
      setNote('');
    } finally {
      setBusy(false);
    }
  };

  if (run.reviewed_at) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          flexWrap: 'wrap',
          padding: '10px 12px',
          borderRadius: 'var(--radius-md)',
          background: 'var(--status-up-soft)',
          fontSize: 'var(--text-sm)',
        }}
      >
        <Icon name="check-circle-2" size={14} style={{ color: 'var(--status-up)', flexShrink: 0 }} />
        <span style={{ flex: 1, minWidth: 0, color: 'var(--text-body)', overflowWrap: 'anywhere' }}>
          Reviewed by {run.reviewed_by_name || 'someone'} · {dateTime(run.reviewed_at)}
          {run.review_note ? <span style={{ color: 'var(--text-muted)' }}> · {run.review_note}</span> : null}
        </span>
        <Button size="sm" variant="ghost" onClick={() => act(false)} disabled={busy}>
          Undo
        </Button>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px', flexWrap: 'wrap' }}>
      <div style={{ flex: '1 1 220px', minWidth: 0 }}>
        <Input
          fieldSize="sm"
          placeholder="Note (optional), e.g. Wordfence findings accepted"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !busy) act(true);
          }}
          disabled={busy}
        />
      </div>
      <Button size="sm" variant="secondary" icon="check" onClick={() => act(true)} disabled={busy}>
        {busy ? 'Saving…' : 'Mark reviewed'}
      </Button>
    </div>
  );
}

// ───────────────────────── Pieces ─────────────────────────

function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: 0 }}>
      <span className="t-eyebrow">{label}</span>
      {/* Wraps on phones, where two facts share a row ("Scheduled · Sun ni…") */}
      <span className="t-num md:truncate" style={{ fontSize: 'var(--text-sm)', color: 'var(--text-body)', overflowWrap: 'anywhere' }}>
        {children}
      </span>
    </div>
  );
}

function Callout({ tone, children }: { tone: 'warn' | 'down'; children: React.ReactNode }) {
  return (
    <div
      style={{
        padding: '10px 12px',
        borderRadius: 'var(--radius-md)',
        background: tone === 'warn' ? 'var(--status-warn-soft)' : 'var(--status-down-soft)',
        color: tone === 'warn' ? 'var(--status-warn)' : 'var(--status-down)',
        fontSize: 'var(--text-sm)',
        overflowWrap: 'anywhere',
      }}
    >
      {children}
    </div>
  );
}

function Empty({ children, tone }: { children: React.ReactNode; tone?: 'down' }) {
  return (
    <div style={{ padding: 'var(--space-4) 0', textAlign: 'center', fontSize: 'var(--text-sm)', color: tone === 'down' ? 'var(--status-down)' : 'var(--text-faint)' }}>
      {children}
    </div>
  );
}
